import React, { useState } from "react";
import { Box, Typography, Tooltip, Link } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventIcon from "@mui/icons-material/EventOutlined";
import {
  DeleteOutlined as DeleteIcon,
  Download as DownloadIcon,
  GroupAdd as GroupAddIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import {
  ProjectTableProps,
  GesamtstatusDomain,
  ProjectSubRow,
} from "../../types";
import { formatNumber, formatCurrency } from "../../utils/formatters";
import {
  getWirtschaftlichkeitChipColor,
  getReifegradChipColor,
} from "../../utils/colors/scoreColor";
import { getGesamtstatusColor } from "../../utils/colors/gesamtstatusColor";
import { getGesamtstatusLabel } from "../../utils/gesamtstatusLabel";
import BaseTable, { TableColumn, TableData } from "./common/BaseTable";
import StatChip from "../common/display/StatChip";
import DetailCell, { detailIconSx } from "../common/display/DetailCell";
import InfoTooltip from "../common/overlays/InfoTooltip";
import CollapsibleColumnButton from "./common/CollapsibleColumnButton";
import { collapsibleSubColumnSx } from "./common/collapsibleColumnStyles";
import ActionsMenu from "../common/overlays/ActionsMenu";
import ConfirmDialog from "../common/feedback/ConfirmDialog";
import ReifegradBreakdown, {
  ReifegradDomain,
} from "./common/ReifegradBreakdown";
import { colors } from "../../styles/colors";

type ExpandableScoreColumn = "wirtschaftlichkeit";

/**
 * Breite des Einzugs vor dem Inhalt einer Unterzeile – so breit wie der Pfeil vor dem Namen des
 * aufgeklappten Projekts: Das Gruppen-Icon steht unter dem Pfeil, der Inhalt unter dem Namen.
 */
const SUB_ROW_INDENT = 26;
/**
 * Wo die Linie der Gruppe läuft: Innenpadding der Zelle (16 px) plus Mitte des Gruppen-Icons
 * (18 px). Sie sitzt an der Zelle, nicht am Einzug, damit sie von Kante zu Kante reicht.
 */
const GROUP_LINE_LEFT = 16 + 9;

/**
 * Gleiche Breite je Domäne, solange die Gewichte noch laden – reine Darstellung. Die fachlichen
 * Gewichte liefert der Server (`reifegrad_gewichte`, Regel `projects/regeln/reifegrad.py`).
 */
const GLEICHE_BREITE = 1;

function buildReifegradDomains(
  project: ProjectTableProps["projects"][number],
  gewichte: ProjectTableProps["reifegradGewichte"],
): ReifegradDomain[] {
  return [
    {
      key: "flaeche",
      label: "Fläche",
      value: project.flaechensicherung_prozent,
      status: project.flaechenakquise_status,
      weight: gewichte?.gewicht_flaeche ?? GLEICHE_BREITE,
    },
    {
      key: "planungsrecht",
      label: "Planungsrecht",
      value: project.planungsrecht_score,
      status: project.planungsrecht_status,
      weight: gewichte?.gewicht_planungsrecht ?? GLEICHE_BREITE,
    },
    {
      key: "genehmigung",
      label: "Genehmigung",
      value: project.genehmigung_score,
      status: project.bimschg_status,
      weight: gewichte?.gewicht_genehmigung ?? GLEICHE_BREITE,
    },
    {
      key: "ausschreibung",
      label: "Ausschreibung",
      value: project.ausschreibung_score,
      status: project.eeg_ausschreibung_status,
      weight: gewichte?.gewicht_ausschreibung ?? GLEICHE_BREITE,
    },
    {
      key: "finanzierung",
      label: "Finanzierung",
      value: project.finanzierung_score,
      status: project.finanzierung_status,
      weight: gewichte?.gewicht_finanzierung ?? GLEICHE_BREITE,
    },
    {
      key: "bau",
      label: "Bau",
      value: project.bau_score,
      status: project.bau_status,
      weight: gewichte?.gewicht_bau ?? GLEICHE_BREITE,
    },
  ];
}

const ProjectTable: React.FC<ProjectTableProps> = ({
  projects,
  onProjectSelect,
  onProjectOpen,
  expandedId,
  onExpand,
  renderExpandedRow,
  subRows,
  renderNameSuffix,
  maxHeight,
  onProjectDeleted,
  onProjectExport,
  exportingProjectId,
  selectedId,
  wirtschaftlichkeitErklaerung,
  reifegradErklaerung,
  reifegradGewichte,
  onProjectShare,
  spaltenSichtbar,
  simulierteIds,
  simulierteSpalten,
}) => {
  /** Gruppe der Unterzeilen, deren Icon gerade überfahren wird – sie wird ganz hervorgehoben */
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  /** Projekt, dessen Löschen gerade bestätigt werden soll */
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [expandedColumns, setExpandedColumns] = useState<
    Set<ExpandableScoreColumn>
  >(new Set());

  const toggleColumn = (column: ExpandableScoreColumn) => {
    setExpandedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(column)) {
        next.delete(column);
      } else {
        next.add(column);
      }
      return next;
    });
  };

  const zeigeWirtschaftlichkeit = spaltenSichtbar?.wirtschaftlichkeit ?? true;
  const zeigeAggregate = spaltenSichtbar?.aggregate ?? true;
  const istAufgeklappt = expandedColumns.has("wirtschaftlichkeit");

  // Die Tabelle läuft mit tableLayout: "fixed" – die Breiten sind also bindend. Aufgeklappt
  // kommen fünf Zahlenspalten dazu; die bekommen feste Pixelbreiten (Zahl + Einheit passen
  // dort immer in eine Zeile), und die Hauptspalten geben den Platz anteilig ab.
  const breite = (zugeklappt: string, aufgeklappt: string) =>
    istAufgeklappt ? aufgeklappt : zugeklappt;

  const columns: TableColumn[] = [
    {
      key: "projekt",
      label: "Projekt",
      align: "left",
      width: breite("20%", "16%"),
      sortable: true,
      sortAccessor: (row) => row.projekt.name,
    },
    {
      // Der Zieltermin der Zeile: beim Projekt die Inbetriebnahme, beim Antrag die
      // geplante Genehmigung bzw. der Bescheid
      key: "targetDate",
      label: "Zieltermin",
      align: "center",
      width: breite("15%", "11%"),
      sortable: true,
    },
    ...(zeigeAggregate
      ? ([
          {
            key: "gesamtstatus",
            label: "Status",
            align: "center",
            width: breite("25%", "20%"),
            sortable: true,
            // Fortschritt statt Text: Rang vom Server (Domäne entlang der Reihenfolge, darin die Stufe)
            sortAccessor: (row) => row.gesamtstatusRang as number,
          },
          {
            key: "reifegrad",
            label: "Reifegrad",
            align: "center",
            width: breite("15%", "10%"),
            headerTooltip: reifegradErklaerung,
            sortable: true,
          },
        ] satisfies TableColumn[])
      : []),
    ...(zeigeWirtschaftlichkeit
      ? ([
          {
            key: "wirtschaftlichkeit",
            label: "Wirtschaftlichkeit",
            align: "center",
            width: breite("15%", "14%"),
            headerTooltip: wirtschaftlichkeitErklaerung,
            sortable: true,
          },
        ] satisfies TableColumn[])
      : []),
    ...(zeigeWirtschaftlichkeit && expandedColumns.has("wirtschaftlichkeit")
      ? ([
          {
            key: "wirtschaftlichkeit_irr",
            label: "IRR",
            align: "right",
            width: "76px",
            isDetailColumn: true,
            sx: collapsibleSubColumnSx.cell,
            sortable: true,
          },
          {
            key: "wirtschaftlichkeit_investition",
            label: "Investition",
            align: "right",
            width: "124px",
            isDetailColumn: true,
            sx: collapsibleSubColumnSx.cell,
            sortable: true,
          },
          {
            key: "wirtschaftlichkeit_ekquote",
            label: "EK",
            align: "right",
            width: "70px",
            isDetailColumn: true,
            sx: collapsibleSubColumnSx.cell,
            sortable: true,
          },
          {
            key: "wirtschaftlichkeit_zins",
            label: "Zins",
            align: "right",
            width: "70px",
            isDetailColumn: true,
            sx: collapsibleSubColumnSx.cell,
            sortable: true,
            headerTooltip:
              "Nach Darlehenshöhe gewichteter Kreditzins über alle Darlehen des Projekts",
          },
          {
            key: "wirtschaftlichkeit_zuschlag",
            label: "Zuschlag",
            align: "right",
            width: "82px",
            isDetailColumn: true,
            sx: collapsibleSubColumnSx.cell,
            sortable: true,
            headerTooltip:
              "Zuschlagswert aus der EEG-Ausschreibung, mit dem das Projekt gerechnet ist (ohne Korrekturfaktor)",
          },
        ] satisfies TableColumn[])
      : []),
  ];

  const tableData: TableData[] = projects.map((project) => ({
    id: project.id,
    istIntern: project.ist_intern,
    projekt: { name: project.name },
    gesamtstatus: project.gesamtstatus,
    gesamtstatusDomain: project.gesamtstatus_domain,
    gesamtstatusRang: project.gesamtstatus_rang,
    gesamtstatusAbgeschlossen: project.gesamtstatus_abgeschlossen,
    leistung: project.gesamtnennleistung_mw,
    targetDate: project.inbetriebnahme,
    reifegrad: project.reifegrad_score,
    wirtschaftlichkeit: project.wirtschaftlichkeit_score,
    wirtschaftlichkeit_irr: project.irr,
    wirtschaftlichkeit_investition: project.investitionskosten,
    wirtschaftlichkeit_ekquote: project.ek_quote,
    wirtschaftlichkeit_zins: project.kreditzins,
    wirtschaftlichkeit_zuschlag: project.zuschlagswert,
    originalData: project,
  }));

  const renderCellInhalt = (
    column: TableColumn,
    _row: TableData,
    value: any,
  ) => {
    switch (column.key) {
      case "projekt": {
        const expanded = expandedId === _row.id;
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Der Pfeil zum Aufklappen erscheint erst beim Überfahren der Zeile und schiebt
                den Namen dabei ein Stück nach rechts; an der aufgeklappten Zeile bleibt er
                stehen, gedreht. Die Breite wächst, nicht das Icon – es behält seine Größe. */}
            {onExpand && (
              <Box
                sx={{
                  display: "flex",
                  flexShrink: 0,
                  overflow: "hidden",
                  width: expanded ? 26 : 0,
                  opacity: expanded ? 1 : 0,
                  transition: "width 0.2s ease, opacity 0.2s ease",
                  "tr:hover &": { width: 26, opacity: 1 },
                }}
              >
                <ChevronRightIcon
                  fontSize="small"
                  sx={{
                    color: colors.text.secondary,
                    transition: "transform 0.2s ease",
                    transform: expanded ? "rotate(90deg)" : "none",
                    flexShrink: 0,
                  }}
                />
              </Box>
            )}
            {onProjectOpen ? (
              // Mit aufklappbaren Zeilen wählt der Klick auf die Zeile aus; geöffnet wird das
              // Projekt über seinen Namen.
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={(event) => {
                  event.stopPropagation();
                  onProjectOpen(_row.id);
                }}
                sx={{
                  fontWeight: "medium",
                  color: colors.text.primary,
                  textAlign: "left",
                }}
              >
                {value.name}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                {value.name}
              </Typography>
            )}
            {_row.leistung !== null && (
              <Typography
                variant="body2"
                noWrap
                sx={{ color: colors.text.secondary, ml: 0.75, flexShrink: 0 }}
              >
                ({formatNumber(_row.leistung, 1)} MW)
              </Typography>
            )}
            {renderNameSuffix?.(_row.id)}
          </Box>
        );
      }

      case "gesamtstatus": {
        if (!value) return "–";
        const gesamtstatusColor = getGesamtstatusColor(
          _row.gesamtstatusDomain,
          _row.gesamtstatusAbgeschlossen,
        );
        const { label, tooltip } = getGesamtstatusLabel(value);
        const chip = (
          <StatChip
            value={label}
            backgroundColor={gesamtstatusColor.background}
            valueColor={gesamtstatusColor.main}
          />
        );
        return tooltip ? (
          <Tooltip title={tooltip}>
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      }

      case "targetDate":
        return (
          <Tooltip title="Inbetriebnahme">
            <Box sx={{ display: "inline-flex", maxWidth: "100%" }}>
              <DetailCell icon={<EventIcon sx={detailIconSx} />}>
                {value ? format(parseISO(value), "dd.MM.yyyy") : "–"}
              </DetailCell>
            </Box>
          </Tooltip>
        );

      case "reifegrad": {
        if (value === null) return "–";
        const scoreColor = getReifegradChipColor(value);
        const domains = buildReifegradDomains(
          _row.originalData,
          reifegradGewichte,
        );
        return (
          <Tooltip title={<ReifegradBreakdown domains={domains} />}>
            <span>
              <StatChip
                value={formatNumber(value / 10, 1)}
                backgroundColor={scoreColor.background}
                valueColor={scoreColor.main}
              />
            </span>
          </Tooltip>
        );
      }

      case "wirtschaftlichkeit": {
        if (value === null) return "–";
        const scoreColor = getWirtschaftlichkeitChipColor(value);
        return (
          <StatChip
            value={formatNumber(value / 10, 1)}
            backgroundColor={scoreColor.background}
            valueColor={scoreColor.main}
          />
        );
      }

      case "wirtschaftlichkeit_irr":
      case "wirtschaftlichkeit_ekquote":
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: collapsibleSubColumnSx.cell.fontSize }}
          >
            {value !== null ? `${formatNumber(value, 1)} %` : "–"}
          </Typography>
        );

      case "wirtschaftlichkeit_investition":
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: collapsibleSubColumnSx.cell.fontSize }}
          >
            {value !== null ? formatCurrency(value) : "–"}
          </Typography>
        );

      case "wirtschaftlichkeit_zins":
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: collapsibleSubColumnSx.cell.fontSize }}
          >
            {value !== null ? `${formatNumber(value, 1)} %` : "–"}
          </Typography>
        );

      case "wirtschaftlichkeit_zuschlag":
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: collapsibleSubColumnSx.cell.fontSize }}
          >
            {value !== null ? `${formatNumber(value, 2)} ct` : "–"}
          </Typography>
        );

      default:
        return value;
    }
  };

  /**
   * Umschlag für Zellen, deren Wert aus einer nicht gespeicherten Simulation stammt.
   *
   * Der Screenshot einer Tabelle wandert weiter als der Hinweis darüber – deshalb muss man
   * auch der einzelnen Zelle ansehen, dass ihr Wert nicht der gespeicherte ist.
   */
  const markiereSimuliert = (
    column: TableColumn,
    row: TableData,
    inhalt: React.ReactNode,
  ) => {
    const istSimuliert =
      simulierteIds?.has(String(row.id)) && simulierteSpalten?.has(column.key);

    if (!istSimuliert) return inhalt;

    return (
      // Natives title statt MUI-Tooltip: BaseTable legt inzwischen selbst einen Tooltip um
      // den Zellinhalt (Anzeige abgeschnittener Texte), und ein äußerer MUI-Tooltip
      // verschluckt den Hover eines inneren.
      <Box
        component="span"
        title="Simulierter Wert – nicht gespeichert"
        sx={{
          display: "inline-block",
          px: 0.75,
          borderRadius: 1,
          fontStyle: "italic",
          // Gestrichelter Rahmen statt nur Tönung: Der Score steht in einem StatChip mit
          // eigener Hintergrundfarbe, die eine Tönung dahinter vollständig verdecken würde.
          border: `1px dashed ${colors.status.info.main}`,
          backgroundColor: colors.status.info.background,
          color: colors.status.info.main,
        }}
      >
        {inhalt}
      </Box>
    );
  };

  const renderCell = (column: TableColumn, row: TableData, value: any) =>
    markiereSimuliert(column, row, renderCellInhalt(column, row, value));

  const renderHeader = (column: TableColumn) => {
    const label = (
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "bold",
          ...(column.isDetailColumn && {
            fontSize: collapsibleSubColumnSx.header.fontSize,
          }),
        }}
        color={column.isDetailColumn ? "text.secondary" : "text.primary"}
      >
        {column.label}
      </Typography>
    );
    const labelWithTooltip = column.headerTooltip ? (
      <InfoTooltip
        title={column.headerTooltip}
        heading={column.label}
        placement="top"
      >
        {label}
      </InfoTooltip>
    ) : (
      label
    );

    if (column.key === "wirtschaftlichkeit") {
      const key = column.key as ExpandableScoreColumn;
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {labelWithTooltip}
          <CollapsibleColumnButton
            isExpanded={expandedColumns.has(key)}
            onToggle={() => toggleColumn(key)}
          />
        </Box>
      );
    }

    return labelWithTooltip;
  };

  const handleRowClick = (row: TableData) => {
    if (onExpand) {
      onExpand(expandedId === row.id ? null : row.id);
      return;
    }
    onProjectSelect?.(row.id);
  };

  // Alle Aktionen eines Projekts im Drei-Punkte-Menü – wie an seinen Unterzeilen
  const renderActions =
    onProjectExport || onProjectShare || onProjectDeleted
      ? (row: TableData) => {
          const isExporting = exportingProjectId === row.id;
          return (
            <ActionsMenu
              ariaLabel={`Aktionen zu ${row.projekt.name}`}
              items={[
                ...(onProjectShare && row.istIntern
                  ? [
                      {
                        label: "Zugriff verwalten",
                        icon: <GroupAddIcon fontSize="small" />,
                        onClick: () => onProjectShare(row.id),
                      },
                    ]
                  : []),
                // Der Export liefert das vollständige Projekt – für Externe nicht vorgesehen
                ...(onProjectExport && row.istIntern
                  ? [
                      {
                        label: isExporting
                          ? "Export läuft …"
                          : "Als Excel exportieren",
                        icon: <DownloadIcon fontSize="small" />,
                        onClick: () => onProjectExport(row.id),
                        loading: isExporting,
                      },
                    ]
                  : []),
                ...(onProjectDeleted
                  ? [
                      {
                        label: "Löschen",
                        icon: <DeleteIcon fontSize="small" />,
                        onClick: () =>
                          setDeleting({
                            id: String(row.id),
                            name: row.projekt.name,
                          }),
                        danger: true,
                        dividerBefore: !!(onProjectShare || onProjectExport),
                      },
                    ]
                  : []),
              ]}
            />
          );
        }
      : undefined;

  /** Unterzeilen mit ihrer Stellung in der Gruppe – für Icon und Linie davor */
  const subRowData = (row: TableData): TableData[] => {
    const rows = subRows?.(String(row.id)) ?? [];
    return rows.map((sub, index) => ({
      id: sub.id,
      sub,
      firstInGroup: rows[index - 1]?.group.key !== sub.group.key,
      lastInGroup: rows[index + 1]?.group.key !== sub.group.key,
      last: index === rows.length - 1,
    }));
  };

  /**
   * Vor dem Inhalt der Projektspalte der Einzug: in der ersten Unterzeile einer Gruppe ihr Icon,
   * von dort eine feine Linie durch alle Unterzeilen der Gruppe wie in einem Dateibaum. Das
   * Icon nennt die Gruppe im Tooltip und hebt beim Überfahren alle ihre Unterzeilen hervor.
   */
  const renderGroupIndent = (subRow: TableData) => {
    const { sub, firstInGroup, lastInGroup } = subRow as TableData & {
      sub: ProjectSubRow;
      firstInGroup: boolean;
      lastInGroup: boolean;
    };
    const line = {
      position: "absolute",
      left: GROUP_LINE_LEFT,
      width: "1px",
      backgroundColor: colors.border.light,
    } as const;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: SUB_ROW_INDENT,
          flexShrink: 0,
        }}
      >
        {firstInGroup ? (
          <>
            <Tooltip title={sub.group.label} placement="left">
              <Box
                onMouseEnter={() => setHoveredGroup(sub.group.key)}
                onMouseLeave={() => setHoveredGroup(null)}
                sx={{
                  display: "flex",
                  color: colors.text.secondary,
                  cursor: "default",
                  "& svg": { fontSize: 18 },
                }}
              >
                {sub.group.icon}
              </Box>
            </Tooltip>
            {!lastInGroup && (
              <Box sx={{ ...line, top: "calc(50% + 12px)", bottom: 0 }} />
            )}
          </>
        ) : (
          <Box sx={{ ...line, top: 0, bottom: lastInGroup ? "50%" : 0 }} />
        )}
      </Box>
    );
  };

  const renderSubRowCell = (column: TableColumn, subRow: TableData) => {
    const { sub } = subRow as TableData & { sub: ProjectSubRow };
    if (column.key !== "projekt") return sub.cells[column.key] ?? null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        {renderGroupIndent(subRow)}
        <Box sx={{ minWidth: 0, flex: 1 }}>{sub.cells.projekt}</Box>
      </Box>
    );
  };

  return (
    <>
      <BaseTable
        columns={columns}
        data={tableData}
        onRowClick={handleRowClick}
        renderCell={renderCell}
        renderHeader={renderHeader}
        renderActions={renderActions}
        // Ohne Überschrift wie in der Nachweisliste: Das Drei-Punkte-Menü spricht für sich, und
        // jeder Knopf trägt seinen Namen („Aktionen zu …“).
        actionsColumnLabel=""
        variant="default"
        showBorder={false}
        emptyMessage="Keine Windpark-Projekte verfügbar"
        selectedRowId={selectedId}
        paginationMode="infinite-scroll"
        renderExpandedRow={
          renderExpandedRow ? (row) => renderExpandedRow(row.id) : undefined
        }
        expandedRowId={expandedId}
        onExpandRow={onExpand}
        getSubRows={subRows ? subRowData : undefined}
        renderSubRowCell={renderSubRowCell}
        renderSubRowActions={(subRow) =>
          (subRow as TableData & { sub: ProjectSubRow }).sub.actions ?? null
        }
        getSubRowSx={(subRow) => {
          const { sub, last } = subRow as TableData & {
            sub: ProjectSubRow;
            last: boolean;
          };
          return {
            "& .MuiTableCell-root": {
              // Trennlinien nur zwischen Projekten: Die Unterzeilen gehören zu ihrem Projekt,
              // erst nach der letzten kommt die Linie zum nächsten.
              ...(last ? {} : { borderBottom: "none" }),
              ...(sub.group.key === hoveredGroup
                ? { backgroundColor: colors.state.hoverPrimary }
                : {}),
            },
          };
        }}
        // Ist ein Projekt aufgeklappt, treten die übrigen zurück – beim Überfahren wieder voll,
        // damit der Wechsel per Klick naheliegt
        getRowSx={(row) => ({
          transition: "opacity 0.2s ease",
          ...(expandedId && row.id !== expandedId
            ? { opacity: 0.45, "&:hover": { opacity: 1 } }
            : {}),
        })}
        keyboardNavigation={!!onExpand}
        scrollRowTo={maxHeight ? "start" : "nearest"}
        stickyHeader={!!maxHeight}
        maxHeight={maxHeight}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Windpark löschen"
        message={
          deleting ? `Windpark „${deleting.name}" wirklich löschen?` : ""
        }
        confirmLabel="Löschen"
        onConfirm={() => {
          if (deleting) onProjectDeleted?.(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
};

export default ProjectTable;
